import React from "react";

import {
  mdiAlertCircleOutline,
  mdiCheckCircleOutline,
  mdiCircleOutline,
  mdiCloseCircleOutline,
  mdiDotsCircle,
  mdiLoading,
} from "@mdi/js";
import Icon, { Stack } from "@mdi/react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

export type BadgeStatus =
  | "unavailable"
  | "neutral"
  | "transcoded"
  | "transcoding"
  | "pending"
  | "error"
  | "warning";

const badgeVariants: Record<BadgeStatus, string> = {
  unavailable: "bg-muted text-muted-foreground/60 border border-border",
  neutral: "bg-card text-muted-foreground border border-border",
  transcoded:
    "bg-status-active/10 text-status-transcoded border border-status-transcoded/20",
  transcoding:
    "text-status-transcoding bg-status-transcoding/5 border border-status-transcoding/20",
  pending:
    "bg-status-queued/5 text-status-queued border border-status-queued/20",
  error: "bg-status-error/5 text-status-error border border-status-error/20",
  warning:
    "bg-status-warning/5 text-status-warning border border-status-warning/20",
};

const countVariants: Record<BadgeStatus, string> = {
  unavailable: "bg-status-neutral text-foreground",
  neutral: "bg-status-neutral text-foreground",
  transcoded: "bg-status-transcoded text-overlay-foreground",
  transcoding: "bg-status-transcoding text-overlay-foreground",
  pending: "bg-status-queued text-overlay-foreground",
  error: "bg-status-error text-overlay-foreground",
  warning: "bg-status-warning text-overlay-foreground",
};

const icons: Record<BadgeStatus, string> = {
  unavailable: mdiCloseCircleOutline,
  neutral: mdiCheckCircleOutline,
  transcoded: mdiCheckCircleOutline,
  transcoding: mdiLoading,
  pending: mdiDotsCircle,
  error: mdiAlertCircleOutline,
  warning: mdiAlertCircleOutline,
};

interface FormatBadgeProps {
  label: string;
  status?: BadgeStatus;
  count?: number;
  expand?: boolean;
  tooltipContent?: string;
  withoutIcon?: boolean;
  withoutTooltip?: boolean;
  onClick?: () => void;
  className?: string;
}

export default function FormatBadge({
  label,
  status = "neutral",
  count,
  expand = false,
  tooltipContent,
  withoutIcon,
  withoutTooltip,
  onClick,
  className,
}: FormatBadgeProps): React.JSX.Element {
  const { t } = useTranslation("editor-content");

  const badgeClassNames = classnames(
    "relative justify-center inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.25 text-[0.78125rem] font-medium",
    expand && "flex-1",
    onClick && "cursor-pointer hover:opacity-70 transition-opacity",
    badgeVariants[status],
    className,
  );
  const countClassNames = classnames(
    "absolute -right-1 -top-1 inline-flex size-4 items-center justify-center rounded-full text-xs font-medium",
    countVariants[status],
  );

  const getTooltipContent = (): string | null => {
    if (withoutTooltip) return null;
    if (tooltipContent) return tooltipContent;
    if (status === "neutral") return null;

    return t(`streamStatus.${status}`);
  };

  const icon =
    status === "transcoding" ? (
      <Stack className="animate-spin" size={0.625}>
        <Icon path={mdiCircleOutline} />
        <Icon className="text-card" path={mdiLoading} />
      </Stack>
    ) : (
      <Icon
        className={status === "pending" ? "animate-pulse" : ""}
        path={icons[status]}
        size={0.625}
      />
    );

  return (
    <div
      className={badgeClassNames}
      data-tooltip-content={getTooltipContent()}
      data-tooltip-id={APP_TOOLTIP_ID}
      onClick={onClick}
    >
      {label}
      {withoutIcon ? null : icon}
      {count !== undefined && count > 1 && (
        <span className={countClassNames}>{count}</span>
      )}
    </div>
  );
}
